import { Api } from "@/api/axios";

export default class AuthApi extends Api {
  constructor(baseUrl, usersEndpointUrl) {
    super();
    this.usersEndpointUrl = baseUrl + usersEndpointUrl;
    this.authenticate = this.authenticate.bind(this);
    this.getAuthenticatedUser = this.getAuthenticatedUser.bind(this);
  }

  /**
   * Authenticate a user.
   */
  authenticate(credentials) {
    return this.post(this.usersEndpointUrl, credentials)
      .then((response) => response.data)
      .catch((error) => {
        throw error;
      });
  }

  /**
   * Register a new account. The API sends a verification mail; the account
   * cannot log in until the address is confirmed.
   */
  register(payload) {
    return this.post(this.usersEndpointUrl + "/register", payload)
      .then((response) => response.data)
      .catch((error) => {
        throw error;
      });
  }

  /**
   * Update the authenticated user's name and email.
   * Resolves to { user, emailChanged }.
   */
  updateProfile(payload) {
    return this.post(this.usersEndpointUrl + "/profile", payload)
      .then((response) => response.data)
      .catch((error) => {
        throw error;
      });
  }

  /**
   * Change the authenticated user's password.
   */
  changePassword(payload) {
    return this.post(this.usersEndpointUrl + "/password", payload)
      .then((response) => response.data)
      .catch((error) => {
        throw error;
      });
  }

  /**
   * Update language / notifications / plan for the authenticated user.
   */
  updateSettings(settings) {
    return this.post(this.usersEndpointUrl + "/settings", settings)
      .then((response) => response.data)
      .catch((error) => {
        throw error;
      });
  }

  /**
   * Save the user's preferred (seed) genres.
   */
  saveGenres(genres) {
    return this.post(this.usersEndpointUrl + "/genres", { genres })
      .then((response) => response.data)
      .catch((error) => {
        throw error;
      });
  }

  /**
   * Log the user out of the application.
   */
  logout() {
    return this.post(this.usersEndpointUrl + "/logout").catch((error) => {
      throw error;
    });
  }

  /**
   * Renew the chat token.
   */
  renewChatToken() {
    return this.post(this.usersEndpointUrl + "/chat/renewToken")
      .then((response) => response.data)
      .catch((error) => {
        throw error;
      });
  }

  /**
   * Checks that a mailed link is genuine, without consuming it.
   * Resolves to { email, wasAlreadyVerified }.
   */
  verify(hash, userId, token) {
    return this.post(this.usersEndpointUrl + "/verify", {
      hash: hash,
      key: userId,
      token: token,
    })
      .then((response) => response.data)
      .catch((error) => {
        throw error;
      });
  }

  /**
   * Confirms an email address from a verification link and spends the token.
   */
  verifyAccount(hash, userId, token) {
    return this.post(this.usersEndpointUrl + "/verify-account", {
      hash: hash,
      key: userId,
      token: token,
    })
      .then((response) => response.data)
      .catch((error) => {
        throw error;
      });
  }

  /**
   * Sends a password reset link to the email, if it belongs to a user.
   * The body carries the honeypot fields; the route runs the same bot filter
   * as register.
   */
  sendPasswordReset(email, payload = {}) {
    return this.post(this.usersEndpointUrl + "/reset-password/" + email, payload)
      .then((response) => response)
      .catch((error) => {
        throw error;
      });
  }

  /**
   * Sends a mail containing a link for user verification.
   */
  sendVerify(userId) {
    return this.post(this.usersEndpointUrl + "/send-verify/" + userId)
      .then((response) => response)
      .catch((error) => {
        throw error;
      });
  }

  /**
   * Sets a new password for a user.
   */
  /**
   * Set a new password from a mailed reset/verification token.
   * PUT, matching the route — this used to POST and 405'd.
   */
  setPassword(userId, data) {
    return this.put(this.usersEndpointUrl + "/set-password/" + userId, data)
      .then((response) => response)
      .catch((error) => {
        throw error;
      });
  }

  /**
   * Get the authenticated user.
   */
  getAuthenticatedUser() {
    return this.get(this.usersEndpointUrl)
      .then((response) => response.data)
      .catch((error) => {
        throw error;
      });
  }
}
